import * as Yup from "yup";
import countries from "../../components/pages/application/rule/countries";
import { filterSafeText, filterNameText, isSafeText, normalizeNameText, SAFE_TEXT_MESSAGE } from "./safeText";
import {
  CITY_REGEX,
  HEADER_NAME_REGEX,
  HOSTNAME_GLOB_REGEX,
  IPV4_REGEX,
  JA3_REGEX,
  JA4_REGEX,
  PATH_GLOB_REGEX,
  UID_REGEX,
  isValidAsn,
  isValidCidr,
  isValidIp,
  isValidIpOrCidrOrRange,
  validationResult,
} from "./patterns";

const COUNTRY_CODES = new Set(countries.map((c) => c.value.toUpperCase()));

/** Shared max lengths — update here to apply across all forms. */
export const FIELD_LIMITS = {
  name: 25,
  roleName: 25,
  personName: 255,
  email: 255,
  passwordMin: 8,
  passwordMax: 255,
  description: 2000,
  blockMessage: 500,
  customRuleValue: 1024,
  cidr: 64,
  hostname: 256,
  headerGlob: 512,
};

export const PASSWORD_COMPLEXITY_MESSAGE =
  "Password must contain at least 8 characters, one uppercase, one number and one special case character";

export const PASSWORD_COMPLEXITY_REGEX =
  /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/;

/** Run a single Yup field schema; returns `{ valid, message? }`. */
export function runFieldValidation(fieldSchema, value) {
  try {
    fieldSchema.validateSync(value);
    return validationResult(true);
  } catch (err) {
    if (err.name !== "ValidationError") throw err;
    return validationResult(false, err.errors?.[0] ?? err.message);
  }
}

/** Wrap a pure validator for use inside Yup `.test()`. */
export function yupFieldTest(validateFn, options) {
  return function (value) {
    const result = validateFn(value, options);
    return result.valid || this.createError({ message: result.message });
  };
}

/** Yup `.test()` — delegates to central isSafeText; empty values defer to `.required()`. */
function yupSafeTextTest({ allowEmpty = true } = {}) {
  return function safeTextTest(value) {
    const trimmed = String(value ?? "").trim();
    if (!trimmed && !allowEmpty) return true;
    if (isSafeText(value, { allowEmpty })) return true;
    return this.createError({ message: SAFE_TEXT_MESSAGE });
  };
}

export function optionalSafeTextField(maxLen, label = "Field", { collapseSpaces = false } = {}) {
  return Yup.string()
    .transform((v) => {
      if (v == null) return "";
      if (collapseSpaces) return normalizeNameText(v, { trim: true });
      return filterSafeText(String(v)).trim();
    })
    .default("")
    .max(maxLen, `${label} must be ${maxLen} characters or fewer`)
    .test("safeText", SAFE_TEXT_MESSAGE, yupSafeTextTest({ allowEmpty: true }));
}

export function requiredSafeTextField(maxLen, label = "Field", { minLen, minMessage, collapseSpaces = false } = {}) {
  let schema = optionalSafeTextField(maxLen, label, { collapseSpaces }).required(`${label} is required`);
  if (minLen != null) {
    schema = schema.min(minLen, minMessage || `${label} must be at least ${minLen} characters`);
  }
  return schema;
}

// ── Form-level field schemas (Yup-first) ─────────────────────────────────────

export const nameField = requiredSafeTextField(FIELD_LIMITS.name, "Name", { collapseSpaces: true });

export const descriptionField = optionalSafeTextField(FIELD_LIMITS.description, "Description");

export const expiresAtField = Yup.string().test(
  "expiresAt",
  "Enter a valid expiration date/time",
  (v) => {
    const trimmed = String(v ?? "").trim();
    return !trimmed || !Number.isNaN(new Date(trimmed).getTime());
  },
);

export const blockMessageField = optionalSafeTextField(FIELD_LIMITS.blockMessage, "Block message");

export const firstNameField = requiredSafeTextField(FIELD_LIMITS.personName, "First name", { collapseSpaces: true });

export const lastNameField = requiredSafeTextField(FIELD_LIMITS.personName, "Last name", { collapseSpaces: true });

export const emailField = Yup.string()
  .trim()
  .email("Invalid email address")
  .required("Email is required")
  .max(FIELD_LIMITS.email, `Email must be ${FIELD_LIMITS.email} characters or fewer`);

export const newUserPasswordField = Yup.string()
  .required("Password is required")
  .min(FIELD_LIMITS.passwordMin, PASSWORD_COMPLEXITY_MESSAGE)
  .max(FIELD_LIMITS.passwordMax, `Password must be ${FIELD_LIMITS.passwordMax} characters or fewer`)
  .matches(PASSWORD_COMPLEXITY_REGEX, PASSWORD_COMPLEXITY_MESSAGE);

export function confirmPasswordField(passwordRef = "password") {
  return Yup.string()
    .required("Please confirm your password")
    .oneOf([Yup.ref(passwordRef)], "Passwords must match");
}

export const passwordChangeField = Yup.string()
  .required("Password is required")
  .min(FIELD_LIMITS.passwordMin, "Must be at least 8 characters")
  .max(FIELD_LIMITS.passwordMax, "Must be 255 characters or fewer");

export function passwordChangeConfirmField(passwordRef = "password") {
  return Yup.string()
    .required("Please confirm your password")
    .test("passwords-match", "Both passwords need to be the same", function (value) {
      return !this.parent[passwordRef] || value === this.parent[passwordRef];
    });
}

export const projectRoleRowField = Yup.object({
  projectId: Yup.string().required("Project is required"),
  roleId: Yup.string().required("Role is required"),
});

// Used when the user is in "role-only" mode; we should not fail validation
// on blank project-role rows, since the save handler will drop invalid
// projectRoles for this mode anyway.
const projectRoleRowOptional = Yup.object({
  projectId: Yup.string().nullable(),
  roleId: Yup.string().nullable(),
});

export const projectRolesField = Yup.array()
  .of(projectRoleRowOptional)
  .nullable()
  .notRequired();

// Used when the user is in "project" mode; require at least one valid
// (projectId, roleId) pair.
export const projectRolesRequiredField = Yup.array()
  .of(projectRoleRowField)
  .min(1, "At least one project assignment is required")
  .required("Project assignments are required");

export const connectionNameField = requiredSafeTextField(FIELD_LIMITS.name, "Connection Name", { collapseSpaces: true });

export const jobTitleField = optionalSafeTextField(FIELD_LIMITS.personName, "Title", { collapseSpaces: true });

export const organisationNameField = requiredSafeTextField(FIELD_LIMITS.personName, "Organisation name", {
  minLen: 4,
  minMessage: "Must be at least 4 characters",
  collapseSpaces: true,
});

export const roleNameField = requiredSafeTextField(FIELD_LIMITS.roleName, "Role name", { collapseSpaces: true });

export const bugTitleField = requiredSafeTextField(200, "Title", {
  minLen: 3,
  minMessage: "Title must be at least 3 characters",
});

export const bugDescriptionField = requiredSafeTextField(5000, "Description", {
  minLen: 3,
  minMessage: "Description must be at least 3 characters",
});

// Imperative adapters — delegate to Yup schemas above.
export const validateName = (value) => runFieldValidation(nameField, value ?? "");
export const validateDescription = (value) => runFieldValidation(descriptionField, value ?? "");
export const validateExpiresAt = (value) => runFieldValidation(expiresAtField, value ?? "");
export const validateBlockMessage = (value) => runFieldValidation(blockMessageField, value ?? "");
export const validateFirstName = (value) => runFieldValidation(firstNameField, value ?? "");
export const validateLastName = (value) => runFieldValidation(lastNameField, value ?? "");
export const validateEmail = (value) => runFieldValidation(emailField, value ?? "");
export const validateJobTitle = (value) => runFieldValidation(jobTitleField, value ?? "");
export const validateOrganisationName = (value) => runFieldValidation(organisationNameField, value ?? "");
export const validateRoleNameField = (value) => runFieldValidation(roleNameField, value ?? "");
export const validateBugTitle = (value) => runFieldValidation(bugTitleField, value ?? "");
export const validateBugDescription = (value) => runFieldValidation(bugDescriptionField, value ?? "");

// ── Match-criterion value validators ──────────────────────────────────────────

export function validateJa3(value, { required = true } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required
      ? validationResult(false, "JA3 fingerprint is required")
      : validationResult(true);
  }
  if (!JA3_REGEX.test(v)) {
    return validationResult(false, "JA3 must be 32 hexadecimal characters");
  }
  return validationResult(true);
}

export function validateJa4(value, { required = true } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required
      ? validationResult(false, "JA4 fingerprint is required")
      : validationResult(true);
  }
  if (!JA4_REGEX.test(v)) {
    return validationResult(false, "JA4 must be 1–64 alphanumeric/underscore characters");
  }
  return validationResult(true);
}

export function validateUid(value, { required = true } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required ? validationResult(false, "UID is required") : validationResult(true);
  }
  if (!UID_REGEX.test(v)) {
    return validationResult(false, "UID must be 1–128 printable characters (no spaces)");
  }
  return validationResult(true);
}

export function validateIpv4Only(value, { required = true } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required
      ? validationResult(false, "IPv4 address is required")
      : validationResult(true);
  }
  if (v.includes("/")) {
    return validationResult(
      false,
      "Enter an IPv4 address only, not a CIDR block (e.g. 1.23.45.67)",
    );
  }
  if (!IPV4_REGEX.test(v)) {
    return validationResult(false, "Enter a valid IPv4 address (e.g. 1.23.45.67)");
  }
  return validationResult(true);
}

export function validateIpOrCidr(value, { required = true, maxLength = FIELD_LIMITS.cidr } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required ? validationResult(false, "CIDR is required") : validationResult(true);
  }
  if (v.length > maxLength) {
    return validationResult(false, `CIDR must be ${maxLength} characters or fewer`);
  }
  if (!isValidIp(v) && !isValidCidr(v)) {
    return validationResult(false, "Enter a valid IPv4/IPv6 address or CIDR (e.g. 203.0.113.0/24)");
  }
  return validationResult(true);
}

export function validateIpOrCidrOrRange(value, { required = true } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required
      ? validationResult(false, "Source IP is required")
      : validationResult(true);
  }
  if (!isValidIpOrCidrOrRange(v)) {
    return validationResult(
      false,
      "Enter a valid source IP (IPv4/IPv6, CIDR, or range, e.g. 10.0.0.0/24)",
    );
  }
  return validationResult(true);
}

export function validatePathGlob(value, { required = true } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required ? validationResult(false, "Path is required") : validationResult(true);
  }
  if (!PATH_GLOB_REGEX.test(v)) {
    return validationResult(false, "Path must start with / (e.g. /admin/**)");
  }
  return validationResult(true);
}

export function validateAsn(value, { required = true } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required ? validationResult(false, "ASN is required") : validationResult(true);
  }
  if (!isValidAsn(v)) {
    return validationResult(false, "ASN must be a non-negative integer up to 4294967295");
  }
  return validationResult(true);
}

export function validateCountryCode(value, { required = true } = {}) {
  const code = String(value ?? "").trim().toUpperCase();
  if (!code) {
    return required
      ? validationResult(false, "Select a valid country")
      : validationResult(true);
  }
  if (!COUNTRY_CODES.has(code)) {
    return validationResult(false, "Select a valid country from the list");
  }
  return validationResult(true);
}

export function validateHeaderName(value, { required = true } = {}) {
  const name = String(value ?? "").trim();
  if (!name) {
    return required
      ? validationResult(false, "Header name is required")
      : validationResult(true);
  }
  if (!HEADER_NAME_REGEX.test(name)) {
    return validationResult(false, "Header name must be 1–128 valid token characters");
  }
  return validationResult(true);
}

export function validateHeaderGlob(value, { maxLength = FIELD_LIMITS.headerGlob } = {}) {
  const glob = String(value ?? "");
  if (glob.length > maxLength) {
    return validationResult(false, `Value glob must be ${maxLength} characters or fewer`);
  }
  return validationResult(true);
}

export function validateBmHeaderFields(headerName, headerGlob) {
  const nameResult = validateHeaderName(headerName);
  if (!nameResult.valid) return nameResult;
  return validateHeaderGlob(headerGlob);
}

export function validateHostnameGlob(value, { required = true, maxLength = FIELD_LIMITS.hostname } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required ? validationResult(false, "Hostname is required") : validationResult(true);
  }
  if (v.length > maxLength) {
    return validationResult(false, `Hostname must be ${maxLength} characters or fewer`);
  }
  if (!HOSTNAME_GLOB_REGEX.test(v)) {
    return validationResult(false, "Enter a valid hostname glob (e.g. *.example.com)");
  }
  return validationResult(true);
}

export function validateCity(value, { required = true } = {}) {
  const v = String(value ?? "").trim();
  if (!v) {
    return required ? validationResult(false, "City is required") : validationResult(true);
  }
  if (!CITY_REGEX.test(v)) {
    return validationResult(false, "City must be 1–128 letters, spaces, hyphens, or apostrophes");
  }
  return validationResult(true);
}
